// Phaser-free formatting helpers shared by every UI screen: display names that
// survive missing data, stat-modifier lines, numbers and times, and a fallback
// ItemDisplay for when the loot describer is unavailable.

import { iconKey, mechKeys } from '../art/keys';
import { art } from '../art/registry';
import { COLORS, rarityColor } from '../config/palette';
import type {
  ItemDisplay,
  ItemInstance,
  MajorUpgradeDef,
  Rarity,
  StatKey,
  StatMod,
  SynergyId,
  Tag,
  WeaponId,
} from '../core/types';
import { EQUIPMENT, MAJOR_UPGRADES, MODS, MUTATIONS, SYNERGIES, WEAPONS } from '../data/items';

// ───────────────────────────── Names ─────────────────────────────

/** 'pulse_carbine' → 'Pulse Carbine'. */
export function titleCase(id: string): string {
  return id
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** 'pulse_carbine' → 'PULSE CARBINE'. */
export function upperName(id: string): string {
  return titleCase(id).toUpperCase();
}

/** Safe lookup into a data table that may still be an empty stub. */
function lookup<T>(table: Record<string, T> | undefined, id: string): T | undefined {
  try {
    return table ? table[id] : undefined;
  } catch {
    return undefined;
  }
}

export function weaponName(id: WeaponId | string | null | undefined): string {
  if (!id) return 'EMPTY';
  return lookup(WEAPONS as Record<string, { name: string }>, id)?.name ?? titleCase(id);
}

export function synergyName(id: SynergyId | string): string {
  return lookup(SYNERGIES as Record<string, { name: string }>, id)?.name ?? titleCase(id);
}

export function synergyColor(id: SynergyId | string): number {
  return lookup(SYNERGIES as Record<string, { color: number }>, id)?.color ?? COLORS.uiGood;
}

export function synergyDesc(id: SynergyId | string): string {
  return lookup(SYNERGIES as Record<string, { desc: string }>, id)?.desc ?? '';
}

export function mutationName(id: string): string {
  return lookup(MUTATIONS as Record<string, { name: string }>, id)?.name ?? titleCase(id);
}

export function equipName(id: string): string {
  return lookup(EQUIPMENT as Record<string, { name: string }>, id)?.name ?? titleCase(id);
}

export function modName(id: string): string {
  return lookup(MODS as Record<string, { name: string }>, id)?.name ?? titleCase(id);
}

export function upgradeDef(id: string): MajorUpgradeDef | undefined {
  return lookup(MAJOR_UPGRADES as Record<string, MajorUpgradeDef>, id);
}

export function itemName(item: ItemInstance): string {
  switch (item.kind) {
    case 'weapon':
      return weaponName(item.defId);
    case 'equipment':
      return equipName(item.defId);
    case 'mod':
      return modName(item.defId);
    case 'mutation':
      return mutationName(item.defId);
  }
}

export function rarityLabel(r: Rarity): string {
  return r.toUpperCase();
}

// ───────────────────────────── Stats ─────────────────────────────

type StatFmt = 'flat' | 'pct' | 'mult' | 'sec' | 'px';
interface StatInfo {
  label: string;
  /** How an 'add' modifier reads: flat number, percentage points, seconds or pixels. */
  fmt: StatFmt;
  lowerIsBetter?: boolean;
}

export const STAT_INFO: Readonly<Record<StatKey, StatInfo>> = {
  maxHp: { label: 'Max HP', fmt: 'flat' },
  maxShield: { label: 'Max shield', fmt: 'flat' },
  shieldRegen: { label: 'Shield regen', fmt: 'flat' },
  shieldDelay: { label: 'Shield delay', fmt: 'sec', lowerIsBetter: true },
  armor: { label: 'Armor', fmt: 'pct' },
  moveSpeed: { label: 'Move speed', fmt: 'mult' },
  jumpPower: { label: 'Jump power', fmt: 'mult' },
  airJumps: { label: 'Air jumps', fmt: 'flat' },
  airDashes: { label: 'Air dashes', fmt: 'flat' },
  dashCooldown: { label: 'Dash cooldown', fmt: 'mult', lowerIsBetter: true },
  dashPower: { label: 'Dash distance', fmt: 'mult' },
  damage: { label: 'Damage', fmt: 'mult' },
  fireRate: { label: 'Fire rate', fmt: 'mult' },
  heatCapacity: { label: 'Heat capacity', fmt: 'mult' },
  cooling: { label: 'Cooling', fmt: 'mult' },
  critChance: { label: 'Crit chance', fmt: 'pct' },
  critMult: { label: 'Crit damage', fmt: 'mult' },
  signatureCooldown: { label: 'Signature cooldown', fmt: 'mult', lowerIsBetter: true },
  ultimateCharge: { label: 'Ultimate charge', fmt: 'mult' },
  chainBonus: { label: 'Chain targets', fmt: 'flat' },
  chainDamage: { label: 'Chain damage', fmt: 'mult' },
  burnPower: { label: 'Burn damage', fmt: 'mult' },
  explosionRadius: { label: 'Explosion radius', fmt: 'mult' },
  explosionDamage: { label: 'Explosion damage', fmt: 'mult' },
  knockback: { label: 'Knockback', fmt: 'mult' },
  knockbackResist: { label: 'Knockback resist', fmt: 'pct' },
  pickupRadius: { label: 'Pickup radius', fmt: 'px' },
  scrapGain: { label: 'Scrap gain', fmt: 'mult' },
  damageTaken: { label: 'Damage taken', fmt: 'mult', lowerIsBetter: true },
};

function signed(n: number, digits = 0): string {
  const v = Number(n.toFixed(digits));
  return `${v >= 0 ? '+' : '-'}${Math.abs(v)}`;
}

/** '+15% Damage' / '-20% Dash cooldown' / '+20 Max HP'. good = better for the player. */
export function formatStatMod(mod: StatMod): { text: string; good: boolean } {
  const info = STAT_INFO[mod.stat] ?? { label: titleCase(String(mod.stat)), fmt: 'flat' as StatFmt };
  let text: string;
  let delta: number;
  if (mod.op === 'mul') {
    delta = mod.value - 1;
    text = `${signed(delta * 100)}% ${info.label}`;
  } else {
    delta = mod.value;
    switch (info.fmt) {
      case 'pct':
      case 'mult':
        text = `${signed(delta * 100)}% ${info.label}`;
        break;
      case 'sec':
        text = `${signed(delta, 1)}s ${info.label}`;
        break;
      default:
        text = `${signed(delta, Math.abs(delta) < 10 && delta % 1 !== 0 ? 1 : 0)} ${info.label}`;
    }
  }
  const good = info.lowerIsBetter ? delta < 0 : delta > 0;
  return { text, good };
}

// ───────────────────────────── Numbers / time ─────────────────────────────

export function formatInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** Compact large numbers: 12,400 → '12.4K'. */
export function formatCompact(n: number): string {
  const v = Math.round(n);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
  return formatInt(v);
}

/** 754 → '12:34', 4000 → '1:06:40'. */
export function formatTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(r).padStart(2, '0')}`;
}

export function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

// ───────────────────────────── Fallback item display ─────────────────────────────

const SLOT_LABEL: Record<string, string> = {
  chest: 'CHEST MODULE',
  shoulders: 'SHOULDER MODULE',
  back: 'BACK MODULE',
  legs: 'LEG MODULE',
};

/** Icon texture key the card should try first for an item (may not exist). */
export function defaultIconKey(item: ItemInstance): string {
  switch (item.kind) {
    case 'weapon':
      return art.weapons[item.defId]?.key ?? mechKeys.weapon(item.defId);
    case 'equipment':
      return iconKey.slot(item.slot);
    case 'mod':
      return iconKey.mod;
    case 'mutation':
      return iconKey.mutation;
  }
}

/**
 * Minimal ItemDisplay built from the item data tables (or just the instance
 * when those are stubs). Used when describeItem() is unavailable or throws.
 */
export function fallbackItemDisplay(item: ItemInstance): ItemDisplay {
  const lines: string[] = [];
  let tags: Tag[] = [];
  let short = '';
  let tradeoff: string | undefined;
  let kindLabel = 'ITEM';
  switch (item.kind) {
    case 'weapon': {
      const def = lookup(WEAPONS as Record<string, (typeof WEAPONS)[WeaponId]>, item.defId);
      kindLabel = 'WEAPON';
      short = def?.short ?? '';
      if (def?.desc) lines.push(def.desc);
      tags = [...(def?.tags ?? [])];
      for (const m of item.mods) {
        lines.push(`Mod: ${modName(m.defId)}`);
        const md = lookup(MODS as Record<string, { tags: Tag[] }>, m.defId);
        for (const t of md?.tags ?? []) if (!tags.includes(t)) tags.push(t);
      }
      if (item.modSlots > item.mods.length) lines.push(`${item.modSlots - item.mods.length} free mod slot${item.modSlots - item.mods.length > 1 ? 's' : ''}`);
      break;
    }
    case 'equipment': {
      const def = lookup(EQUIPMENT as Record<string, (typeof EQUIPMENT)[keyof typeof EQUIPMENT]>, item.defId);
      kindLabel = SLOT_LABEL[item.slot] ?? 'MODULE';
      if (def?.desc) lines.push(def.desc);
      for (const s of def?.stats ?? []) lines.push(formatStatMod(s).text);
      tradeoff = def?.tradeoff;
      tags = [...(def?.tags ?? [])];
      break;
    }
    case 'mod': {
      const def = lookup(MODS as Record<string, (typeof MODS)[keyof typeof MODS]>, item.defId);
      kindLabel = def?.altMode ? 'WEAPON MOD · ALT FIRE' : 'WEAPON MOD';
      if (def?.desc) lines.push(def.desc);
      tradeoff = def?.tradeoff;
      tags = [...(def?.tags ?? [])];
      break;
    }
    case 'mutation': {
      const def = lookup(MUTATIONS as Record<string, (typeof MUTATIONS)[keyof typeof MUTATIONS]>, item.defId);
      kindLabel = 'MUTATION';
      if (def?.desc) lines.push(def.desc);
      for (const s of def?.stats ?? []) lines.push(formatStatMod(s).text);
      tradeoff = def?.drawback;
      tags = [...(def?.tags ?? [])];
      break;
    }
  }
  for (const a of item.affixes ?? []) {
    if (a.negative) tradeoff = tradeoff ? `${tradeoff} ${a.label}` : a.label;
    else lines.push(a.label);
  }
  return {
    name: itemName(item),
    kindLabel,
    rarity: item.rarity,
    rarityLabel: rarityLabel(item.rarity),
    color: rarityColor(item.rarity),
    short,
    lines,
    tradeoff,
    tags,
    icon: { key: defaultIconKey(item) },
  };
}

/** Fallback display for a boss-reward major upgrade. */
export function fallbackUpgradeDisplay(def: MajorUpgradeDef): ItemDisplay {
  const lines: string[] = [];
  if (def.desc) lines.push(def.desc);
  for (const s of def.stats ?? []) lines.push(formatStatMod(s).text);
  return {
    name: def.name || titleCase(def.id),
    kindLabel: 'MAJOR UPGRADE',
    rarity: 'prototype',
    rarityLabel: def.mech ? `${def.mech.toUpperCase()} ONLY` : 'BOSS REWARD',
    color: COLORS.ultBar,
    short: '',
    lines,
    tags: [],
    icon: { key: iconKey.upgrade },
  };
}
